import { useMemo, useState } from 'react';

import { History, Search, X } from 'lucide-react';

import { ActivityEntry } from '@/components/activity/ActivityEntry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useActivityLog, useActivityLogFacets } from '@/hooks/useActivityLog';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuthStore } from '@/stores/auth.store';

import {
  ACTIVITY_ACTION_LABELS,
  ACTIVITY_ACTOR_TYPE_LABELS,
  ACTIVITY_ACTOR_TYPES,
  ACTIVITY_ENTITY_TYPE_LABELS,
} from '@repo/shared-types';

import type { ActivityActorType, ActivityLogQuery } from '@repo/shared-types';

const ANY = '__any__';

const PAGE_SIZE = 25;

/**
 * The platform-wide audit trail.
 *
 * Built around the question that prompted it: something changed, nobody knows
 * who. So the two filters that lead are actor and entity type, and MCP entries
 * are one click away — that is how you tell an operator's edit from the AI
 * tooling's.
 */
export const ActivityLogPage = () => {
  usePageTitle('Activity Log');

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState(ANY);
  const [action, setAction] = useState(ANY);
  const [actorType, setActorType] = useState(ANY);
  const [actorId, setActorId] = useState(ANY);
  const [detailedOnly, setDetailedOnly] = useState(false);

  const query = useMemo<Partial<ActivityLogQuery>>(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(search ? { search } : {}),
      ...(entityType !== ANY ? { entity_type: entityType } : {}),
      ...(action !== ANY ? { action } : {}),
      ...(actorType !== ANY ? { actor_type: actorType as ActivityActorType } : {}),
      ...(actorId !== ANY ? { actor_id: actorId } : {}),
      ...(detailedOnly ? { detailed_only: true } : {}),
    }),
    [page, search, entityType, action, actorType, actorId, detailedOnly],
  );

  const { data, isLoading, isError } = useActivityLog(query, isAdmin);
  const { data: facets } = useActivityLogFacets(isAdmin);

  const hasFilters =
    Boolean(search) ||
    entityType !== ANY ||
    action !== ANY ||
    actorType !== ANY ||
    actorId !== ANY ||
    detailedOnly;

  const resetFilters = () => {
    setSearch('');
    setEntityType(ANY);
    setAction(ANY);
    setActorType(ANY);
    setActorId(ANY);
    setDetailedOnly(false);
    setPage(1);
  };

  /** Changing a filter has to reset paging, or page 7 of a new filter is empty. */
  const onFilterChange = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Activity Log</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          The activity log is restricted to administrators.
        </p>
      </div>
    );
  }

  const entries = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <History className="h-6 w-6" />
        <div>
          <h1 className="text-2xl font-semibold">Activity Log</h1>
          <p className="text-muted-foreground text-sm">
            Every change made across the platform — who made it, when, and what the values were
            before and after.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search by name, user or summary…"
            className="pl-8"
          />
        </div>

        <Select value={entityType} onValueChange={onFilterChange(setEntityType)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All modules" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All modules</SelectItem>
            {(facets?.entity_types ?? []).map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {ACTIVITY_ENTITY_TYPE_LABELS[item.value] ?? item.value} ({item.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={actorType} onValueChange={onFilterChange(setActorType)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Anyone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Anyone</SelectItem>
            {ACTIVITY_ACTOR_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {ACTIVITY_ACTOR_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={actorId} onValueChange={onFilterChange(setActorId)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All users</SelectItem>
            {(facets?.actors ?? [])
              .filter((actor) => actor.actor_id)
              .map((actor) => (
                <SelectItem key={actor.actor_id} value={actor.actor_id as string}>
                  {actor.actor_name || actor.actor_email || actor.actor_id} ({actor.count})
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Select value={action} onValueChange={onFilterChange(setAction)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All actions</SelectItem>
            {(facets?.actions ?? []).map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {ACTIVITY_ACTION_LABELS[item.value] ?? item.value} ({item.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={detailedOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setDetailedOnly((prev) => !prev);
            setPage(1);
          }}
          title="Hide entries that have no field-level before/after diff"
        >
          Detailed only
        </Button>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            <X className="mr-1 h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {isLoading && <p className="text-muted-foreground py-10 text-sm">Loading activity…</p>}

      {isError && <p className="py-10 text-sm text-red-500">Failed to load the activity log.</p>}

      {!isLoading && !isError && entries.length === 0 && (
        <div className="text-muted-foreground flex flex-col items-center gap-2 py-16 text-sm">
          <History className="h-8 w-8 opacity-40" />
          <p>No activity matches these filters.</p>
        </div>
      )}

      <div className="space-y-2">
        {entries.map((entry) => (
          <ActivityEntry key={entry.id} entry={entry} />
        ))}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-muted-foreground text-xs">
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} entries
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogPage;
